import {
  observable,
  action,
  computed,
  runInAction,
  makeObservable,
} from "mobx";
import nanoid from "nanoid/non-secure";
import { debounce } from "throttle-debounce";

import NotificationsApi from "../api/NotificationsApi";

import headerApiUrlsFallback from "../utils/headerApiUrlsFallback";

import notificationSound from "../sounds/notification-sound.mp3";

import NotificationError from "./models/NotificationError";
import callWindowFunction from "../utils/callWindowFunction";
import logger from "../logger";

const loadNotificationsBy = 10;
const cancelString = "cancel";
const updateLag = 60000;
const randomLag = 20000;
const notificationsDropdownId = "notifications";

class Notifications {
  constructor(rootStore, notificationsUrl) {
    makeObservable(this, {
      notificationError: observable,
      notificationsNumber: observable,
      isNotificationSoundActive: observable,
      notifications: observable,
      notificationsTotal: observable,
      loadingNotifications: observable,
      extraNewNotifications: observable,
      showNotificationFromPush: observable,
      archivedNotificationsLoadMore: observable,
      loadingMoreNotifications: observable,
      notificationsReadRequest: observable,
      notificationFromPush: observable,
      showingArchiveNotifications: observable,
      notificationSubmenuDropdownMargin: observable,
      showNotificationDropdown: computed,
      setShowNotificationDropdown: action,
      setNotificationSubmenuDropdownMargin: action,
      makeNotificationsScrollbarRef: action,
      setNotificationError: action,
      clearNotificationError: action,
      handleWebPushNotifications: action,
      closeUpdateInterval: action,
      loadNotificationFromPush: action,
      reloadMuteOption: action,
      startUpdateIntervalNotifications: action,
      clearNotificationsInterval: action,
      setNotificationsNumber: action,
      updateNotificationsTotal: action,
      loadMoreNotifications: action,
      onNotificationsDropdownToggle: action,
      markAllNotificationsAsRead: action,
      toggleSound: action,
      handleNotificationsMount: action,
      handleNotificationsUnmount: action,
      getNotificationFromId: action,
      handleNotificationsScrollbar: action,
      readNotificationsDebounce: action,
      onShowRegularNotifications: action,
      loadNotifications: action,
      loadArchivedNotifications: action,
      loadUnArchivedNotifications: action,
      loadExtraNewNotifications: action,
      unsubscribe: action,
    });

    this.rootStore = rootStore;
    const notificationUrl = headerApiUrlsFallback(
      notificationsUrl,
      "notifications"
    );

    this.NotificationsApi = new NotificationsApi(notificationUrl);

    this.requestCancels = {};
    this.loadNotificationsRequest = false;
    this.readNotificationsTimeout = null;
    this.readNotifications = [];
    this.reloadNotificationsInterval = null;

    this.randomInterval = null;
    this.isUpdateIntervalClosed = false;

    this.notificationSoundPlayer = new Audio(notificationSound);

    if (this.notificationSoundPlayer.addEventListener) {
      this.notificationSoundPlayer.addEventListener("canplaythrough", () => {
        /* аудио может быть воспроизведено */
        this.canPlaySound = true;
      });
    } else {
      this.canPlaySound = true;
    }

    this.matchMediaNotificationOverlay =
      !!window.matchMedia && window.matchMedia("(max-width: 600px)");
  }

  notificationError = null;

  notificationsNumber = undefined;

  isNotificationSoundActive = undefined;

  notifications = [];

  notificationsTotal = 0;

  loadingNotifications = true;

  extraNewNotifications = 0;

  showNotificationFromPush = false;

  /** индикатор(toggler) кнопочки "Показать еще" */
  archivedNotificationsLoadMore = true;

  loadingMoreNotifications = false;

  notificationsReadRequest = 0;

  notificationFromPush = null;

  showingArchiveNotifications = false;

  // Отступ для дропдауна нотификаций в сабменю
  // изменяется в сторе submenuInfo зависимости от отображенных элементов
  // small, medium, large
  notificationSubmenuDropdownMargin = "small";

  canPlaySound = false;

  errorMessage = null;

  // Проиграть звук уведомления
  // если прокинут параметр isForced, звук будет проигран без учета поля isMaster
  playSound = (isForced) => {
    const { isMaster } = this.rootStore.crossDomain;

    if (
      this.isNotificationSoundActive &&
      (isForced || isMaster) &&
      this.notificationSoundPlayer
    ) {
      this.notificationSoundPlayer.pause();
      this.notificationSoundPlayer.currentTime = 0;
      this.notificationSoundPlayer.play().catch((error) => {
        console.log(error);

        runInAction(() => {
          this.errorMessage = error;
          this.canPlaySound = false;
        });

        if (isMaster) {
          callWindowFunction("publishToEventBus", [
            {
              subject: "frontNotificationPlaySound",
              destination: "exceptThisTab",
              body: {},
            },
          ]);
        }
      });
    }
  };

  getSoundInfo = () => [
    `Can play sound: ${this.canPlaySound}`,
    `Sound error message: ${this.errorMessage}`,
  ];

  setNotificationSubmenuDropdownMargin = (firmNameVisible, codeVisible) => {
    if (!firmNameVisible && !codeVisible) {
      this.notificationSubmenuDropdownMargin = "small";
      return;
    }

    if (codeVisible && !firmNameVisible) {
      this.notificationSubmenuDropdownMargin = "medium";
      return;
    }

    this.notificationSubmenuDropdownMargin = "large";
  };

  makeNotificationsScrollbarRef = (ref) => {
    this.notificationsScrollbarRef = ref;
  };

  setNotificationError = ({ name, retryFunc }) => {
    if (!this.checkErrorByName(name)) {
      this.notificationError = new NotificationError({ name, retryFunc });
    }
  };

  clearNotificationError = (errorName) => {
    if (errorName === "all" || this.checkErrorByName(errorName)) {
      this.notificationError = null;
    }
  };

  checkErrorByName = (name) =>
    !(this.notificationError === null || this.notificationError.name !== name);

  handleWebPushNotifications = (id) => {
    this.loadingNotifications = true;
    this.showNotificationFromPush = true;
    this.notificationFromPush = null;

    this.loadNotificationFromPush(id);

    if (!this.showNotificationDropdown) {
      this.setShowNotificationDropdown(true);
    }
  };

  updateInterval = (func, lag) => {
    if (this.isUpdateIntervalClosed) {
      return;
    }

    const randomInterval = Math.floor(Math.random() * randomLag);

    setTimeout(() => {
      func();
      this.updateInterval(func, lag);
    }, lag + randomInterval);
  };

  closeUpdateInterval = () => {
    this.isUpdateIntervalClosed = true;
  };

  loadNotificationFromPush = (id) => {
    this.requestCancels.getNotificationByIdRequest &&
      this.requestCancels.getNotificationByIdRequest(cancelString);

    const { promise, cancel } = this.NotificationsApi.getById(id);
    this.requestCancels.getNotificationByIdRequest = cancel;

    promise
      .then(({ data: notification }) => {
        this.clearNotificationError("loadNotificationFromPush");

        runInAction(() => {
          delete this.requestCancels.getNotificationByIdRequest;

          this.loadingNotifications = false;
          this.notificationFromPush = notification;

          if (notification.IsUnread) {
            this.notificationFromPush.IsUnread = false;

            this.NotificationsApi.markAsRead([
              {
                id: notification.ID,
                deliveryid: notification.DeliveryId || null,
              },
            ]).catch((error) => {
              console.error("readNotifications error", error);

              this.notificationFromPush.IsUnread = true;
            });
          }
        });
      })
      .catch((error) => {
        runInAction(() => {
          delete this.requestCancels.getNotificationByIdRequest;
          this.setNotificationError({
            name: "loadNotificationFromPush",
            retryFunc: () => {
              this.loadNotificationFromPush(id);
            },
          });
          if (error !== cancelString) {
            this.loadingNotifications = false;
            console.error("loadNotificationFromPush", error);
          }
        });
      });
  };

  /**
   * Метод обновления счетчика колокольчика - notificationsNumber.
   * Тут же обновляем общее кол-во непрочитанных уведомления
   * для пагинации - notificationsTotal
   * */
  reloadNotifications = () => {
    this.NotificationsApi.getUnreadCount()
      .then(({ data }) => {
        this.updateNotificationsTotal(data.Total);
        callWindowFunction("publishToEventBus", [
          {
            subject: "frontNotificationPollingMessage",
            destination: "exceptThisTab",
            tabId: this.rootStore.profile.tabId,
            body: data.Total,
          },
        ]);
        callWindowFunction("publishToEventBus", [
          {
            subject: "frontNotificationsCounters",
            destination: "exceptThisTab",
            tabId: this.rootStore.profile.tabId,
            body: {
              total: data.Total,
              important: data.Important,
            },
          },
        ]);
        this.rootStore.feeds.updateImportantCounter(data.Important);
        this.rootStore.feeds.initNotificationList(data.Important);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * Метод прочтения и удаления одного уведомления
   * @param { string } - id удаляемого уведомления
   */
  removeNotification = (id) => {
    let notificationIndex = null;
    let isUnread = false;

    for (let i = 0; i < this.notifications.length; i += 1) {
      if (this.notifications[i].ID === id) {
        notificationIndex = i;
        isUnread = this.notifications[i].IsUnread;
        break;
      }
    }

    // Если не прочитанно по какой-то причине, читаем, по успеху удаляем.
    if (isUnread) {
      const notification = this.getNotificationFromId(id);
      this.NotificationsApi.markAsRead([
        {
          id,
          deliveryid: (notification && notification.DeliveryId) || null,
        },
      ])
        .then(() =>
          runInAction(() => {
            this.spliceNotification(notificationIndex);
          })
        )
        .catch((error) => {
          console.error("remove notification error!", error);
        });
    } else {
      setTimeout(
        () =>
          runInAction(() => {
            this.spliceNotification(notificationIndex);
          }),
        0
      );
    }
  };

  /**
   * Метод фактического удаления уведомления из стора на фронте
   * @param { number } index - индекс удаляемого уведомления в массиве.
   */
  spliceNotification = (index) => {
    if (index !== null) {
      this.notifications.splice(index, 1);
    }
  };

  reloadMuteOption() {
    callWindowFunction("getStoredValue", [
      `mute_option_${this.rootStore.profile.userId}`,
      ({ value }) => {
        if (value === null) {
          runInAction(() => {
            this.isNotificationSoundActive = true;
          });
        } else {
          runInAction(() => {
            if (value && value.result) {
              this.isNotificationSoundActive = value.result;
            }
          });
        }
      },
    ]);
    callWindowFunction("subscribeToValue", [
      `mute_option_${this.rootStore.profile.userId}`,
      ({ value }) => {
        runInAction(() => {
          this.isNotificationSoundActive = value.result;
        });
      },
    ]);
  }

  startUpdateIntervalNotifications = () => {
    this.isUpdateIntervalClosed = false;

    this.updateInterval(this.reloadNotifications, updateLag);
  };

  clearNotificationsInterval = () => {
    if (!this.reloadNotificationsInterval) return;
    window.clearInterval(this.reloadNotificationsInterval);
    this.reloadNotificationsInterval = null;
  };

  setNotificationsNumber = (value) => {
    if (value > this.notificationsNumber) {
      this.playSound();
    }

    this.notificationsNumber = value;
  };

  /**
   * Обновление общего количества новых уведомлений с учетом
   * уже загруженных но непрочитанных.
   * Запускается этот метод по прочтению уведомления через wss socket.onmessage
   * или при интервальном опрашивании кол-ва новых уведомлений
   * @param { number } - notificationsNumber,
   * которое получаем в результате метода this.NotificationsApi.getUnreadCount
   * */
  updateNotificationsTotal = (total) => {
    const unreadNotifications = this.notifications.filter(
      (item) => item.IsUnread
    ).length;
    const totalUnread = total - unreadNotifications;

    if (
      total > this.notificationsNumber &&
      this.showNotificationDropdown &&
      !this.showNotificationFromPush
    ) {
      this.extraNewNotifications += total - this.notificationsNumber;
    }

    this.setNotificationsNumber(total);

    // Новые на бэке минус новые на фронте, чтоб понять сколько новых еще не бэке
    this.notificationsTotal = unreadNotifications
      ? (totalUnread > 0 && totalUnread) || 0
      : total;
  };

  /**
   * Метод подгрузки кусочков уведомлений (пейджер)
   */
  loadMoreNotifications = () => {
    // Собираем ID всех непрочитанных уведомлений
    const unredItems = this.notifications
      .filter((item) => item.IsUnread)
      .map((item) => item.ID);

    if (unredItems.length) {
      // Помечаем как прочитанные на фронте
      this.markIsUnread(unredItems, false);

      this.notificationsReadRequest += 1;
      this.notificationsNumber -= unredItems.length;

      // И отправляем запрос на прочтение на бэке
      this.NotificationsApi.markAsRead(
        unredItems.map((id) => {
          const notification = this.getNotificationFromId(id);
          return {
            id,
            deliveryid: (notification && notification.DeliveryId) || null,
          };
        })
      )
        .then(() => {
          runInAction(() => {
            this.notificationsReadRequest -= 1;

            // Если все хорошо, загружаем новую пачку уведомлений, которые
            // добавятся в общий список в выпадашке.
            this.loadNotifications();
          });
        })
        .catch((error) => {
          runInAction(() => {
            this.notificationsReadRequest -= 1;
            this.notificationsNumber += unredItems.length;

            // Если не успешно, то отмечаем уведомления как непрочитанные на фронте.
            this.markIsUnread(unredItems, true);
          });
          console.error("mark notification as read error!", error);
        });

      return;
    }

    this.loadNotifications();
  };

  onNotificationsDropdownToggle = (value) => {
    if (value) {
      this.rootStore.webPush.showWebPushPopup();
    }

    this.clearNotificationError("all");

    this.rootStore.header.toogleOverlayOnMobile(
      this.matchMediaNotificationOverlay,
      value
    );
  };

  setShowNotificationDropdown = (value) => {
    const { header } = this.rootStore;

    if (value) {
      this.rootStore.webPush.showWebPushPopup();

      header.toggleDropdown(notificationsDropdownId);
    } else if (header.openedDropdown === notificationsDropdownId) {
      header.closeDropdown();
    }

    this.clearNotificationError("all");
  };

  get showNotificationDropdown() {
    const { header } = this.rootStore;

    return header.openedDropdown === notificationsDropdownId;
  }

  /**
   * Метод прочтения всех уведомлений
   */
  markAllNotificationsAsRead = () => {
    this.NotificationsApi.markAllAsRead()
      .then(() => {
        runInAction(() => {
          this.clearNotificationError("markAllNotificationsAsRead");

          this.notificationsNumber = 0;
          this.notificationsTotal = 0;
          this.archivedNotificationsLoadMore = true;

          this.notifications.forEach((item) => {
            item.IsUnread = false;
          });
        });
      })
      .catch((error) => {
        this.setNotificationError({
          name: "markAllNotificationsAsRead",
          retryFunc: this.markAllNotificationsAsRead,
        });

        console.error("markAllNotificationsAsRead", error);
      });
  };

  toggleSound = () => {
    this.isNotificationSoundActive = !this.isNotificationSoundActive;

    callWindowFunction("putValue", [
      `mute_option_${this.rootStore.profile.userId}`,
      { result: this.isNotificationSoundActive },
    ]);
  };

  /**
   * componentDidMount тултипа уведомлений
   */
  handleNotificationsMount = () => {
    if (!this.showNotificationFromPush) {
      this.loadNotifications();
    }
  };

  /**
   * componentWillUnmount тултипа уведомлений
   */
  handleNotificationsUnmount = () => {
    Object.keys(this.requestCancels).forEach((request) => {
      this.requestCancels[request](cancelString);
    });

    if (this.getNotificationByIdRequest) {
      this.getNotificationByIdRequest.cancel();
      this.getNotificationByIdRequest = null;
    }

    this.notificationsUnreadCount = 0;
    this.loadNotificationsRequest = false;

    this.notifications = [];
    this.loadingNotifications = true;
    this.notificationsTotal = this.notificationsNumber;
    this.loadingMoreNotifications = false;
    this.archivedNotificationsLoadMore = true;
    this.extraNewNotifications = 0;
    this.showNotificationFromPush = false;
    this.notificationFromPush = null;
  };

  // TODO: экшн на будущее
  onRemoveNotification = () => {};

  getNotificationFromId = (id) =>
    this.notifications.filter((notification) => notification.ID === id)[0] ||
    null;

  handleNotificationsScrollbar = () =>
    this.notificationsScrollbarRef &&
    this.notificationsScrollbarRef.wrappedInstance &&
    this.notificationsScrollbarRef.wrappedInstance.handleScrollbar();

  /**
   * Метод для пометки уведомленя как прочитанные на фронте
   * @param { string } id уведомления
   * @param { boolean } bool
   */
  markIsUnread = (id, bool) => {
    if (Array.isArray(id)) {
      // Пока полагаемся на ws
      // this.notificationsNumber -= id.length;
      this.notifications.forEach((item) => {
        if (id.includes(item.ID)) {
          item.IsUnread = !!bool;
        }
      });
    } else {
      for (let i = 0; i < this.notifications.length; i += 1) {
        if (id === this.notifications[i].ID) {
          this.notifications[i].IsUnread = !!bool;

          break;
        }
      }
    }
  };

  /**
   * Собираем и отправляем(асинхронно) id-ки на прочтение(пачкой)
   * в определенный промежуток времени.
   * @param { string } notificationId id уведомления
   */
  readNotificationsDebounce = (notificationId) => {
    const isUnread = this.notifications.some(
      (item) => item.ID === notificationId && item.IsUnread
    );

    // Если действительно непрочитанно уведомление
    if (isUnread) {
      this.loadNotificationsRequest = true;
      clearTimeout(this.readNotificationsTimeout);

      // Формируем массив id для отправки на прочтение
      if (!this.readNotifications.includes(notificationId)) {
        this.readNotifications.push(notificationId);

        this.markIsUnread(notificationId, false); // Помечаем как прочитанное
      }

      this.readNotificationsTimeout = setTimeout(() => {
        const ids = this.readNotifications;

        runInAction(() => {
          this.notificationsReadRequest += 1;
          this.readNotifications = [];
        });

        this.NotificationsApi.markAsRead(
          ids.map((id) => {
            const notification = this.getNotificationFromId(id);
            return {
              id,
              deliveryid: (notification && notification.DeliveryId) || null,
            };
          })
        )
          .then(() => {
            runInAction(() => {
              this.notificationsReadRequest -= 1;
              this.loadNotificationsRequest = !!this.notificationsReadRequest;

              this.handleNotificationsScrollbar();
            });
          })
          .catch((error) => {
            console.error("readNotifications error", error);

            runInAction(() => {
              this.notificationsReadRequest -= 1;
              this.loadNotificationsRequest = !!this.notificationsReadRequest;

              this.markIsUnread(ids, true);
              this.handleNotificationsScrollbar();
            });
          });
        // TODO: подкорректировать дебаунс от нагрузки на сервер
      }, 300);
    }
  };

  onShowRegularNotifications = () => {
    this.getNotificationByIdRequest && this.getNotificationByIdRequest.cancel();

    this.notifications = [];
    this.loadingNotifications = true;
    this.extraNewNotifications = 0;
    this.showNotificationFromPush = false;
    this.notificationFromPush = null;

    this.loadNotifications();
  };

  /**
   * Метод загрузки уведомлений при появлении тултипа и переключении
   * прочитанных/непрочитанных уведомлений
   * */
  loadNotifications = () => {
    if (this.notificationsTotal) {
      !this.loadNotificationsRequest && this.loadUnArchivedNotifications();
    } else {
      !this.loadNotificationsRequest && this.loadArchivedNotifications();
    }
  };

  /**
   * Метод загрузки прочитанных уведомлений
   */
  loadArchivedNotifications = debounce(300, false, () => {
    this.loadNotificationsRequest = true;
    const readNotifications = this.notifications.filter(
      (item) => !item.IsUnread
    );
    const requesId = nanoid();
    const { promise, cancel } = this.NotificationsApi.getArchived(
      readNotifications.length,
      loadNotificationsBy
    );
    this.requestCancels[requesId] = cancel;

    promise
      .then(({ data }) => {
        runInAction(() => {
          this.clearNotificationError("loadArchivedNotifications");

          this.loadNotificationsRequest = false;
          delete this.requestCancels[requesId];
          const result = this.preventNotificationsDuplication(
            this.notifications,
            data
          );

          this.notifications = [...this.notifications, ...result];
          // archivedNotificationsLoadMore - Кнопочка "показать еще"
          this.archivedNotificationsLoadMore =
            data.length === loadNotificationsBy;
          this.loadingNotifications = false;

          this.handleNotificationsScrollbar();
        });
      })
      .catch((error) => {
        runInAction(() => {
          delete this.requestCancels[requesId];
          this.setNotificationError({
            name: "loadArchivedNotifications",
            retryFunc: this.loadArchivedNotifications,
          });
          if (error !== cancelString) {
            this.loadNotificationsRequest = false;
            console.error("loadArchivedNotifications error", error);
          }
        });
      });
  });

  loadUnArchivedNotifications = () => {
    this.loadNotificationsRequest = true;
    const requesId = nanoid();
    const { promise, cancel } = this.NotificationsApi.getUnarchived(
      0,
      loadNotificationsBy
    );
    this.requestCancels[requesId] = cancel;

    promise
      .then(({ data }) => {
        runInAction(() => {
          this.clearNotificationError("loadUnArchivedNotifications");

          delete this.requestCancels[requesId];
        });

        const result = this.preventNotificationsDuplication(
          this.notifications,
          data
        );

        /** Если элементов меньше чем шаг пагинации, догружаем из прочитанных. */
        if (result.length < loadNotificationsBy) {
          /** Сколько нужно догрузить из прочитанных */
          const archivedItemsLength = loadNotificationsBy - result.length;
          /** Собираем все прочитанные на данном шаге, чтоб скипнуть уже загруженные */
          const readNotifications = this.notifications.filter(
            (item) => !item.IsUnread
          );

          const { promise: archivedPromise, cancel: archivedCancel } =
            this.NotificationsApi.getArchived(
              readNotifications.length,
              archivedItemsLength
            );

          const archivedRequesId = nanoid();

          runInAction(() => {
            this.requestCancels[archivedRequesId] = archivedCancel;
          });

          archivedPromise
            .then(({ data: archivedData }) => {
              runInAction(() => {
                /** Обнуляем состояние сетевого запроса */
                this.loadNotificationsRequest = false;
                delete this.requestCancels[archivedRequesId];

                /** На всякий случай делаем дедубликацию прочитанных уведомлений */
                const response = this.preventNotificationsDuplication(
                  this.notifications,
                  archivedData
                );

                this.notifications = [
                  ...this.notifications,
                  ...result,
                  ...response,
                ];
                this.loadingNotifications = false;
                /** Обнуляем переменную счетчика пагинации для новых */
                this.notificationsTotal = 0;
                /** Обновляем переменную счетчика пагинации для прочитанных */
                this.archivedNotificationsLoadMore =
                  archivedData.length === archivedItemsLength;

                this.handleNotificationsScrollbar();
              });
            })
            .catch((error) => {
              runInAction(() => {
                delete this.requestCancels[archivedRequesId];
                this.loadingNotifications = false;
                if (error !== cancelString) {
                  this.loadNotificationsRequest = false;
                  console.error("getArchived error", error);
                }
              });
            });
        } else {
          runInAction(() => {
            this.loadNotificationsRequest = false;
            delete this.requestCancels[requesId];

            this.notifications = [...this.notifications, ...result];
            this.loadingNotifications = false;

            const unreadNotifications = this.notifications.filter(
              (item) => item.IsUnread
            );
            const totalUnread =
              this.notificationsTotal - unreadNotifications.length;
            /** Обновляем переменную счетчика пагинации для новых */
            this.notificationsTotal = (totalUnread > 0 && totalUnread) || 0;

            this.handleNotificationsScrollbar();
          });
        }
      })
      .catch((error) => {
        runInAction(() => {
          delete this.requestCancels[requesId];

          this.setNotificationError({
            name: "loadUnArchivedNotifications",
            retryFunc: this.loadUnArchivedNotifications,
          });

          if (error !== cancelString) {
            this.loadNotificationsRequest = false;
            console.error("loadUnArchivedNotifications", error);
          }
        });
      });
  };

  /**
   * Метод подгрузки сверхновых уведомлений при открытой выпадашке уведомлений
   */
  loadExtraNewNotifications = () =>
    new Promise((resolve, reject) => {
      runInAction(() => {
        this.loadingMoreNotifications = true;
      });

      const requesId = nanoid();
      const { promise, cancel } = this.NotificationsApi.getUnarchived(
        0,
        loadNotificationsBy
      );

      runInAction(() => {
        this.requestCancels[requesId] = cancel;
      });

      promise
        .then(({ data }) => {
          this.clearNotificationError("loadExtraNewNotifications");

          runInAction(() => {
            delete this.requestCancels[requesId];

            const result = this.preventNotificationsDuplication(
              this.notifications,
              data
            );

            this.notifications = [...this.notifications, ...result]
              // Сортируем нотификации, для того, чтоб новые добавились в самый верх списка
              .sort(
                (a, b) =>
                  new Date(b.CreatedOn).getTime() -
                  new Date(a.CreatedOn).getTime()
              );
            this.loadingMoreNotifications = false;
            this.extraNewNotifications = 0;

            resolve();
          });
        })
        .catch((error) => {
          runInAction(() => {
            this.setNotificationError({
              name: "loadExtraNewNotifications",
              retryFunc: this.loadExtraNewNotifications,
            });

            delete this.requestCancels[requesId];

            this.loadingMoreNotifications = false;
            reject(error);
          });

          console.error("loadExtraNewNotifications error", error);
        });
    });

  /**
   * Метод фильтрации уже загруженных уведомлений,
   * для предотвращения дублирования уведомлений
   * @param { Array } notifications - уведомления в сторе
   * @param { Array } result - новые уведомления
   */
  preventNotificationsDuplication = (notifications = [], result = []) => {
    const notificationsToObj = {};
    notifications.forEach((item) => {
      notificationsToObj[item.ID] = item;
    });
    return (
      (Array.isArray(result) &&
        result.filter((item) => !notificationsToObj[item.ID])) ||
      []
    );
  };

  /**
   * Метод отписки от автоматической рассылки. Пока не используется,
   * так как нет отписки от конкретного партнера на стороне бэка
   * @param { string } id - ID нотификации
   * @param { number } partnerId - ID партнёра
   * @param { string } partnerName - Название партнёра
   */
  unsubscribe = (id, partnerId, partnerName) => {
    this.NotificationsApi.unsubscribe(partnerId, id)
      .then(() => {
        logger.showNotification({
          text: `Вы успешно отписались от рассылки ${partnerName}`,
          type: "success",
        });
        this.removeNotification(id);

        if (this.showNotificationFromPush) {
          runInAction(() => {
            this.showNotificationFromPush = false;
          });
        }
      })
      .catch((error) => {
        logger.showNotification({
          text: "Что-то пошло не так. Повторите попытку позднее.",
          type: "error",
        });
        logger.error(error, {
          sentryTitle: "Отписка от автоматической рассылки",
        });
      });
  };
}

export default Notifications;
